import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { take } from 'rxjs';
import { AssistantService } from './assistant.service';
import { formatErrorMessage, getUserEmployeeId } from 'src/app/core/functions';
import { AssistantConversationFB, FirebaseService } from 'src/app/core/services/firebase.service';
import { environment } from 'src/environments/environment';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  error?: boolean;
}

@Component({
  selector: 'app-assistant',
  templateUrl: './assistant.component.html',
  styleUrls: ['./assistant.component.scss'],
  standalone: false,
})
export class AssistantComponent implements OnInit {
  open = false;
  view: 'chat' | 'history' = 'chat';
  question = '';
  sending = false;
  loadingHistory = false;

  employeeId: string | null = null;
  conversations: AssistantConversationFB[] = [];
  currentId: string | null = null;

  @ViewChild('thread') threadRef: ElementRef<HTMLElement>;
  @ViewChild('field') fieldRef: ElementRef<HTMLTextAreaElement>;

  constructor(
    private assistantService: AssistantService,
    private afAuth: AngularFireAuth,
    private firebaseService: FirebaseService
  ) {}

  ngOnInit(): void {
    this.afAuth.idTokenResult.pipe(take(1)).subscribe((user) => {
      if (!user) return;
      this.employeeId = getUserEmployeeId(user.claims);
      if (!this.employeeId) return;
      // Se recupera el historial (y con él la última conversación) para que un refresco de
      // página, o entrar desde otro dispositivo, no la pierda.
      this.firebaseService
        .getAssistantConversations(this.employeeId)
        .pipe(take(1))
        .subscribe({
          next: (conversations) => {
            this.conversations = conversations;
            this.currentId = conversations[0]?.id ?? null;
          },
          error: (err) => {
            if (!environment.production) console.log('No se ha podido cargar el historial del asistente', err);
          },
        });
    });
  }

  get currentMessages(): ChatMessage[] {
    return this.current()?.messages ?? [];
  }

  private current(): AssistantConversationFB | undefined {
    return this.conversations.find((c) => c.id === this.currentId);
  }

  /** Para distinguir en el listado, entre varias conversaciones con el mismo título (p.ej. la
   * misma pregunta repetida tras un fallo), cuál terminó realmente en un error. */
  lastFailed(conversation: AssistantConversationFB): boolean {
    return !!conversation.messages[conversation.messages.length - 1]?.error;
  }

  toggle() {
    this.open = !this.open;
    if (this.open) {
      setTimeout(() => this.fieldRef?.nativeElement.focus(), 60);
    }
  }

  close() {
    this.open = false;
  }

  showHistory() {
    this.view = 'history';
  }

  backToChat() {
    this.view = 'chat';
  }

  openConversation(conversation: AssistantConversationFB) {
    this.currentId = conversation.id;
    this.view = 'chat';
  }

  deleteConversation(conversation: AssistantConversationFB, event: Event) {
    event.stopPropagation();
    this.conversations = this.conversations.filter((c) => c.id !== conversation.id);
    if (this.currentId === conversation.id) this.currentId = this.conversations[0]?.id ?? null;
    this.firebaseService.deleteAssistantConversation(conversation.id).catch(() => {
      if (!environment.production) console.log('No se ha podido borrar la conversación del asistente');
    });
  }

  newConversation() {
    // No se guarda hasta que tenga algún mensaje: si no, cada clic en "+" sin escribir nada iría
    // acumulando conversaciones vacías en el historial.
    this.currentId = null;
    this.view = 'chat';
    this.question = '';
    setTimeout(() => this.fieldRef?.nativeElement.focus(), 60);
  }

  when(iso: string): string {
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return 'ahora';
    if (minutes < 60) return `hace ${minutes} min`;
    if (minutes < 60 * 24) return `hace ${Math.round(minutes / 60)} h`;
    if (minutes < 60 * 48) return 'ayer';
    return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  }

  send() {
    const question = this.question.trim();
    if (!question || this.sending || !this.employeeId) return;

    let conversation = this.current();
    if (!conversation) {
      const now = new Date().toISOString();
      conversation = {
        id: crypto.randomUUID(),
        employeeId: this.employeeId,
        title: question.length > 42 ? question.slice(0, 42) + '…' : question,
        messages: [],
        lastMessageAt: now,
        createdOn: now,
      };
      this.conversations.unshift(conversation);
      this.currentId = conversation.id;
    }

    conversation.messages.push({ role: 'user', text: question });
    this.question = '';
    this.sending = true;
    this.scrollToBottom();

    this.assistantService
      .ask(question)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          conversation!.messages.push({ role: 'assistant', text: response.answer });
          this.persist(conversation!);
        },
        error: (error) => {
          conversation!.messages.push({
            role: 'assistant',
            text: formatErrorMessage(error, 'No se ha podido contactar con el asistente. Inténtalo de nuevo.'),
            error: true,
          });
          // No se persiste: si el fallo se guardara en Firestore, esa conversación quedaría con
          // el error clavado para siempre (se volvería a ver cada vez que se reabra desde el
          // historial, en esta sesión o en cualquier otra, sin poder "arreglarlo"). Se queda solo
          // visible en la sesión actual para que el usuario pueda simplemente reintentar.
          this.sending = false;
          this.scrollToBottom();
        },
      });
  }

  private persist(conversation: AssistantConversationFB) {
    conversation.lastMessageAt = new Date().toISOString();
    this.conversations.sort((a, b) => (a.lastMessageAt < b.lastMessageAt ? 1 : -1));
    this.sending = false;
    this.scrollToBottom();

    this.firebaseService.saveAssistantConversation(conversation).catch(() => {
      if (!environment.production) console.log('No se ha podido guardar la conversación del asistente');
    });
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = this.threadRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }
}
